import React, { createContext, useContext, useState } from "react";
import { SUBISLANDS } from "../../../pages/MentalPlusPages/IslandRegistry";
import { useIslandPreferences } from "../../../hooks/useIslandPreferences";
import { useAuth } from "../../../contexts/AuthContext";
import { useTranslation } from "react-i18next";



type VisibilityState = Record<string, boolean>;
type TitleState = Record<string, string>;

const IslandVisibilityContext = createContext<{
  visibility: VisibilityState;
  titles: TitleState;
  setVisibility: (key: string, value: boolean) => void;
  setTitle: (key: string, value: string) => void;
  getTitle: (key: string) => string;
}>({
  visibility: {},
  titles: {},
  setVisibility: () => {},
  setTitle: () => {},
  getTitle: (k) => k,
});

export const IslandVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth(); // <-- adapt
  const { t } = useTranslation();
  const userKey = user?.id ?? "anon";
  const {
    visibility: storedVisibility,
    titles: storedTitles,
    setVisibility: persistVisibility,
    setTitle: persistTitle,
  } = useIslandPreferences(`islandPrefs_v1_${userKey}`);

  const [visibility, setVisibilityState] = useState<VisibilityState>(storedVisibility);
  const [titles, setTitlesState] = useState<TitleState>(storedTitles);

  React.useEffect(() => {
  setVisibilityState(storedVisibility);
  setTitlesState(storedTitles);
}, [storedVisibility, storedTitles]);

  const setVisibility = (key: string, value: boolean) => {
    setVisibilityState((prev) => {
      const updated = { ...prev, [key]: value };

      // Parent → children
      if (SUBISLANDS[key]) {
        SUBISLANDS[key].forEach((sub) => {
          updated[sub.key] = value;
          persistVisibility(sub.key, value);
        });
      } else {
        // Child → parent
        const parentKey = Object.keys(SUBISLANDS).find((parent) =>
          SUBISLANDS[parent].some((sub) => sub.key === key)
        );

        if (parentKey) {
          const siblings = SUBISLANDS[parentKey];
          const allOff = siblings.every((sub) =>
            sub.key === key ? !value : updated[sub.key] === false
          );
          updated[parentKey] = !allOff;
          persistVisibility(parentKey, !allOff);
        }
      }

      persistVisibility(key, value);
      return updated;
    });
  };

  const setTitle = (key: string, value: string) => {
    setTitlesState((t) => ({ ...t, [key]: value }));
    persistTitle(key, value);
  };

  const getDefaultTitle = (key: string) => {
    const translationKey = `mentalplus_tab_ile_${key}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : key;
  };
  const getTitle = (key: string) => {
    return titles[key]?.trim() ? titles[key] : getDefaultTitle(key);
  };

  return (
    <IslandVisibilityContext.Provider
      value={{ visibility, titles, setVisibility, setTitle, getTitle }}
    >
      {children}
    </IslandVisibilityContext.Provider>
  );
};

export const useIslandVisibility = () => useContext(IslandVisibilityContext);
